import React from "react";
import { Helmet } from "react-helmet-async";
import { ShieldCheckIcon as ShieldCheck, CurrencyDollarIcon as CurrencyDollar, CrownIcon as Crown, SparkleIcon as Sparkle, ChartLineUpIcon as ChartLineUp, VideoCameraIcon as VideoCamera, CheckCircleIcon as CheckCircle, StorefrontIcon as Storefront, UsersIcon as Users, StampIcon as Stamp } from "@phosphor-icons/react";
import ReelStage, {
  Beat,
  beatProgress,
  easeOutCubic,
  easeInOutCubic,
} from "./ReelStage";
import { WaxSeal } from "@/components/luxury";

/**
 * Seller-side marketing reel — 15s, 1080×1920 (or 1920×1080 with `?o=horizontal`).
 *
 * Story arc:
 *   0.0–2.6 — Hook: ink + gold stamp + the breeder headline.
 *   2.6–5.4 — Dashboard mockup: KPIs ramp up via tabular tickers.
 *   5.4–8.2 — 7-step verification checklist stamps tick by tick.
 *   8.2–10.8 — Stripe payout card slides up (incl. 20% Connect referral).
 *   10.8–13.4 — Tools strip: live tours, pedigree certs, master pedigree.
 *   13.4–15.0 — Closing WaxSeal + wordmark (rendered by ReelStage).
 *
 * Designed in lockstep with the buyer reel — same accents, same closing,
 * different beats. Together they form a 30-second back-to-back evergreen
 * that updates whenever the platform UI changes (since we use real
 * primitives — WaxSeal, Indicium-tone chips, gold gradients).
 */
export default function MarketingReelSeller() {
  return (
    <>
      <Helmet>
        <title>Reel — Seller · Armstrong K9 Registry</title>
        <meta name="robots" content="noindex" />
        <style>{`
          html, body, #root { background: #050505; margin: 0; overflow: hidden; }
        `}</style>
      </Helmet>
      <ReelStage
        accentColor="#C9A227"
        brandLine={"Run your kennel\nlike the brand it is."}
      >
        <BeatHook />
        <BeatDashboardKPIs />
        <BeatVerification />
        <BeatPayout />
        <BeatToolsStrip />
      </ReelStage>
    </>
  );
}

/* -------------------------------------------------------------- */
/* Beat 1 — Hook                                                   */
/* -------------------------------------------------------------- */
function BeatHook() {
  return (
    <Beat start={0.0} end={2.4}>
      {(p) => {
        const ep = easeOutCubic(p);
        const sealOpen = beatProgress(p, 0.05, 0.55);
        const sealScale = 0.4 + easeOutCubic(sealOpen) * 0.6;
        const headlineMask = beatProgress(p, 0.35, 1.0);
        const kickerOpacity = Math.min(1, beatProgress(p, 0.1, 0.4));
        return (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-20 text-center">
            <div
              className="relative"
              style={{
                transform: `scale(${sealScale})`,
                filter: `drop-shadow(0 8px 40px rgba(201,162,39,${0.2 + sealOpen * 0.45}))`,
              }}
            >
              <WaxSeal size={140} title="Armstrong K9 Registry" />
            </div>
            <p
              className="mt-12 uppercase font-bold text-[20px] tracking-[0.45em] text-gold-light"
              style={{ opacity: kickerOpacity }}
            >
              For Breeders
            </p>
            <h1
              className="mt-10 font-spectral font-light text-white"
              style={{
                fontFamily: '"Spectral", Georgia, serif',
                fontSize: 110,
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                maxWidth: 920,
                clipPath: `inset(0 ${(1 - easeInOutCubic(headlineMask)) * 100}% 0 0)`,
                transform: `translateY(${(1 - ep) * 12}px)`,
              }}
            >
              Run your kennel<br />like a brand.
            </h1>
            <div
              className="mt-12 h-px"
              style={{
                width: easeOutCubic(headlineMask) * 360,
                background:
                  "linear-gradient(90deg, transparent, rgba(201,162,39,0.95), transparent)",
              }}
            />
          </div>
        );
      }}
    </Beat>
  );
}

/* -------------------------------------------------------------- */
/* Beat 2 — Dashboard KPIs                                         */
/* -------------------------------------------------------------- */
function BeatDashboardKPIs() {
  // Three numbers ramp up while the dashboard chrome rises into view.
  // Numbers cap at the listed values; the easing makes them feel like
  // an analytics dashboard counting up live.
  const targets = [
    { label: "Active litters", value: 7, prefix: "" },
    { label: "Waitlisted buyers", value: 142, prefix: "" },
    { label: "Revenue YTD", value: 84600, prefix: "$" },
  ];
  return (
    <Beat start={2.4} end={5.4}>
      {(p) => {
        const ep = easeInOutCubic(p);
        const tickerP = Math.min(1, beatProgress(p, 0.15, 0.95));
        const slideY = (1 - ep) * 120;
        return (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-14">
            <p className="uppercase tracking-[0.35em] text-[16px] text-crimson font-bold mb-6">
              The Kennel Dashboard
            </p>
            <h2
              className="font-spectral font-light text-white text-center mb-12"
              style={{
                fontFamily: '"Spectral", Georgia, serif',
                fontSize: 64,
                lineHeight: 1.05,
              }}
            >
              Your business. One screen.
            </h2>

            {/* Mock dashboard surface */}
            <div
              className="w-[940px] rounded-2xl bg-cream text-ink ring-1 ring-gold/40 shadow-[0_30px_80px_rgba(0,0,0,0.6)] overflow-hidden"
              style={{
                transform: `translateY(${slideY}px)`,
                opacity: Math.min(1, ep * 1.3),
              }}
            >
              <div className="px-7 py-5 border-b border-[#E2DAC2] flex items-center justify-between bg-white">
                <div className="flex items-center gap-3">
                  <Crown size={26} weight="duotone" className="text-gold" />
                  <span
                    className="font-spectral text-[26px] tracking-tight"
                    style={{ fontFamily: '"Spectral", Georgia, serif' }}
                  >
                    Heritage Cane Corso
                  </span>
                  <WaxSeal size={28} title="Verified" />
                </div>
                <span className="text-[14px] text-ink/60 font-mono">{new Date().getFullYear()} · Q4</span>
              </div>
              <div className="grid grid-cols-3 divide-x divide-[#E2DAC2] bg-cream">
                {targets.map((t) => (
                  <KPI key={t.label} t={t} progress={tickerP} />
                ))}
              </div>
              {/* Mini sparkline-ish bar row */}
              <div className="px-7 py-6 bg-white border-t border-[#E2DAC2]">
                <div className="flex items-end gap-2 h-24">
                  {Array.from({ length: 16 }).map((_, i) => {
                    const h = 18 + (Math.sin(i * 0.9) + 1.2) * 30;
                    const reveal = Math.max(0, Math.min(1, tickerP * 1.6 - i * 0.05));
                    return (
                      <div
                        key={i}
                        className="flex-1 rounded-t-md bg-gold-gradient"
                        style={{
                          height: h * reveal,
                          opacity: 0.4 + reveal * 0.6,
                          background: "linear-gradient(180deg, #E6D070 0%, #C9A227 100%)",
                        }}
                      />
                    );
                  })}
                </div>
                <div className="flex items-center justify-between mt-3 text-[14px] text-ink/55">
                  <span>Inquiries · 30d</span>
                  <span className="font-mono">+38%</span>
                </div>
              </div>
            </div>
          </div>
        );
      }}
    </Beat>
  );
}

function KPI({ t, progress }) {
  const v = Math.floor(t.value * easeOutCubic(progress));
  const display = v.toLocaleString();
  return (
    <div className="px-7 py-7">
      <p className="text-[12px] uppercase tracking-[0.25em] text-ink/55 font-bold">{t.label}</p>
      <p
        className="mt-2 font-spectral font-light tracking-tight font-tabular"
        style={{
          fontFamily: '"Spectral", Georgia, serif',
          fontSize: 52,
          lineHeight: 1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {t.prefix}{display}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------- */
/* Beat 3 — Verification stamps                                    */
/* -------------------------------------------------------------- */
const VERIFICATION_STEPS = [
  "Identity confirmed",
  "Kennel name registered",
  "Address verified",
  "Microchip uniqueness",
  "Health screening on file",
  "References called",
  "Premises inspection",
];

function BeatVerification() {
  return (
    <Beat start={5.4} end={8.2}>
      {(p) => {
        const ep = easeInOutCubic(p);
        const slideX = (1 - ep) * 60;
        return (
          <div className="absolute inset-0 flex items-center justify-center px-20">
            <div className="grid grid-cols-[auto,1fr] gap-16 items-center max-w-[920px]">
              {/* Left: pulsing seal with halo */}
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute"
                  style={{
                    inset: -120,
                    background:
                      "radial-gradient(circle at center, rgba(201,162,39,0.45) 0%, transparent 60%)",
                    filter: "blur(6px)",
                  }}
                />
                <div
                  style={{
                    transform: `scale(${0.85 + Math.sin(p * Math.PI * 2) * 0.04 + ep * 0.08})`,
                  }}
                >
                  <WaxSeal size={260} title="Verified Kennel" />
                </div>
              </div>
              {/* Right: 7-step checklist with staggered tick stamps */}
              <div>
                <p className="uppercase tracking-[0.35em] text-[16px] text-crimson font-bold">
                  7-step verification
                </p>
                <h2
                  className="mt-3 font-spectral font-light text-white"
                  style={{
                    fontFamily: '"Spectral", Georgia, serif',
                    fontSize: 56,
                    lineHeight: 1.05,
                  }}
                >
                  Earn the seal.<br />Wear it for life.
                </h2>
                <ul className="mt-9 flex flex-col gap-3">
                  {VERIFICATION_STEPS.map((step, i) => {
                    const stepDelay = 0.05 + i * 0.105;
                    const stepP = Math.max(0, ep - stepDelay) / Math.max(0.01, 1 - stepDelay);
                    const stamped = stepP > 0;
                    return (
                      <li
                        key={step}
                        className="flex items-center gap-4"
                        style={{
                          opacity: Math.min(1, stepP * 2.5),
                          transform: `translateX(${(1 - Math.min(1, stepP * 1.6)) * slideX}px)`,
                        }}
                      >
                        <span
                          className="flex items-center justify-center rounded-full"
                          style={{
                            width: 36,
                            height: 36,
                            background: stamped
                              ? "linear-gradient(135deg, #E6D070 0%, #C9A227 100%)"
                              : "rgba(255,255,255,0.08)",
                            transform: `scale(${0.6 + Math.min(1, stepP * 1.6) * 0.4})`,
                            boxShadow: stamped
                              ? "0 4px 12px rgba(201,162,39,0.35), inset 0 1px 0 rgba(255,255,255,0.4)"
                              : "none",
                          }}
                        >
                          <CheckCircle
                            size={22}
                            weight="bold"
                            className={stamped ? "text-ink" : "text-white/30"}
                          />
                        </span>
                        <span
                          className="text-[26px] text-white/90"
                          style={{ fontFamily: '"Spectral", Georgia, serif', fontWeight: 300 }}
                        >
                          {step}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        );
      }}
    </Beat>
  );
}

/* -------------------------------------------------------------- */
/* Beat 4 — Stripe payout                                          */
/* -------------------------------------------------------------- */
function BeatPayout() {
  return (
    <Beat start={8.2} end={10.8}>
      {(p) => {
        const ep = easeOutCubic(p);
        const slide = (1 - ep) * 80;
        const cardOpacity = Math.min(1, p * 2.2);
        const numberP = Math.min(1, beatProgress(p, 0.2, 0.85));
        const total = Math.floor(easeOutCubic(numberP) * 4940);
        const referralAmt = Math.floor(easeOutCubic(numberP) * 1040);
        return (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-16">
            <p
              className="uppercase tracking-[0.35em] text-[16px] text-crimson font-bold"
              style={{ opacity: cardOpacity }}
            >
              Paid in two days
            </p>
            <h2
              className="mt-4 font-spectral font-light text-white text-center"
              style={{
                fontFamily: '"Spectral", Georgia, serif',
                fontSize: 64,
                lineHeight: 1.05,
                opacity: cardOpacity,
              }}
            >
              Stripe Connect.<br />20% Buyer-Pass referrals.
            </h2>

            <div
              className="mt-14 w-[820px] rounded-2xl bg-cream text-ink ring-1 ring-gold/55 shadow-[0_30px_80px_rgba(0,0,0,0.6)] overflow-hidden"
              style={{
                opacity: cardOpacity,
                transform: `translateY(${slide}px)`,
              }}
            >
              <div className="px-8 py-6 border-b border-[#E2DAC2] flex items-center justify-between bg-white">
                <div className="flex items-center gap-3">
                  <CurrencyDollar size={26} weight="duotone" className="text-gold" />
                  <span
                    className="font-spectral text-[26px] tracking-tight"
                    style={{ fontFamily: '"Spectral", Georgia, serif' }}
                  >
                    Payout · this week
                  </span>
                </div>
                <span className="text-[14px] text-ink/60 font-mono">acct_••••3982</span>
              </div>
              <div className="px-8 py-7 grid grid-cols-2 gap-y-3 gap-x-8 text-[20px]">
                <Row label="Otto · Buyer Pass deposit" amount="+ $1,200" />
                <Row label="Aria · final settlement" amount="+ $3,800" />
                <Row label="Roman · referral · 20%" amount={`+ $${referralAmt.toLocaleString()}`} accent />
                <Row label="Stripe processing" amount="– $84" muted />
              </div>
              <div className="px-8 py-5 bg-[#FAF6EE] flex items-center justify-between">
                <span className="uppercase text-[13px] tracking-[0.25em] font-bold text-ink/65">
                  Net payout
                </span>
                <span
                  className="font-spectral text-[40px] tracking-tight font-tabular"
                  style={{
                    fontFamily: '"Spectral", Georgia, serif',
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  ${total.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        );
      }}
    </Beat>
  );
}

function Row({ label, amount, accent, muted }) {
  return (
    <div className="flex items-center justify-between border-b border-dashed border-[#E2DAC2] pb-2 last:border-0">
      <span className={muted ? "text-ink/55" : "text-ink/85"}>{label}</span>
      <span
        className={`font-mono ${
          accent ? "text-gold-dark font-semibold" : muted ? "text-ink/55" : "text-ink"
        }`}
      >
        {amount}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------- */
/* Beat 5 — Tools strip                                            */
/* -------------------------------------------------------------- */
const TOOLS = [
  { icon: VideoCamera, label: "Live video tours", sub: "Daily.co · 15 min" },
  { icon: Stamp, label: "Pedigree certificates", sub: "PDF + PNG export" },
  { icon: ChartLineUp, label: "Master pedigree graph", sub: "Six generations" },
  { icon: Users, label: "Buyer waitlist", sub: "First-right matchmaking" },
  { icon: Storefront, label: "Marketplace listings", sub: "Public + searchable" },
  { icon: Sparkle, label: "Verified WaxSeal", sub: "Earned, not bought" },
];

function BeatToolsStrip() {
  return (
    <Beat start={10.8} end={13.4}>
      {(p) => {
        const ep = easeOutCubic(p);
        return (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-14">
            <p className="uppercase tracking-[0.35em] text-[16px] text-crimson font-bold">
              Built into the Registry
            </p>
            <h2
              className="mt-4 font-spectral font-light text-white text-center"
              style={{
                fontFamily: '"Spectral", Georgia, serif',
                fontSize: 60,
                lineHeight: 1.05,
              }}
            >
              Every tool a kennel<br />actually uses.
            </h2>
            <div className="mt-12 grid grid-cols-2 gap-5 w-[940px]">
              {TOOLS.map((tool, i) => {
                const delay = i * 0.07;
                const tp = Math.max(0, ep - delay) / Math.max(0.01, 1 - delay);
                const reveal = Math.min(1, tp * 1.6);
                return (
                  <div
                    key={tool.label}
                    className="flex items-center gap-5 px-6 py-5 rounded-xl bg-cream text-ink ring-1 ring-gold/35 shadow-[0_8px_30px_rgba(0,0,0,0.4)]"
                    style={{
                      opacity: reveal,
                      transform: `translateY(${(1 - reveal) * 26}px)`,
                    }}
                  >
                    <span
                      className="flex items-center justify-center rounded-lg flex-shrink-0"
                      style={{
                        width: 56,
                        height: 56,
                        background: "#FBF1D8",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 2px 10px rgba(201,162,39,0.18)",
                        border: "1px solid rgba(201,162,39,0.55)",
                      }}
                    >
                      <tool.icon size={28} weight="duotone" className="text-gold-dark" />
                    </span>
                    <div className="min-w-0">
                      <p
                        className="font-spectral text-[26px] tracking-tight"
                        style={{ fontFamily: '"Spectral", Georgia, serif' }}
                      >
                        {tool.label}
                      </p>
                      <p className="text-[15px] text-ink/60 mt-1">{tool.sub}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }}
    </Beat>
  );
}
